import type {
  IPolymorphicComponentPropsWithRef,
  IPolymorphicRef,
  IWithAsProp,
} from '@sixui/ui';
import type { FieldValues, UseControllerProps } from 'react-hook-form';
import { forwardRef, useContext } from 'react';
import { useMergeRefs } from '@floating-ui/react';
import { LabeledContext } from '@sixui/ui';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getFormErrorText } from '~/helpers/getFormErrorText';

// TODO: should live in a sixui-forms package

export type IFormFieldOwnProps<TFieldValues extends FieldValues> =
  UseControllerProps<TFieldValues> & {
    readOnly?: boolean;
    required?: boolean;
    emptyValue?: unknown;
    hasError?: boolean;
    errorText?: string;
    forwardError?: boolean;
  };

export type IFormFieldProps<
  TFieldValues extends FieldValues,
  TRoot extends React.ElementType,
> = IPolymorphicComponentPropsWithRef<TRoot, IFormFieldOwnProps<TFieldValues>>;

type IFormField = <
  TFieldValues extends FieldValues,
  TRoot extends React.ElementType,
>(
  props: IFormFieldProps<TFieldValues, TRoot>,
) => React.ReactNode;

export const FormField: IFormField = forwardRef(function FormField<
  TFieldValues extends FieldValues,
  TRoot extends React.ElementType,
>(
  props: IFormFieldProps<TFieldValues, TRoot>,
  forwardedRef?: IPolymorphicRef<TRoot>,
) {
  const {
    as: Component = 'input',
    name,
    shouldUnregister,
    defaultValue,
    control,
    rules: rulesProp,
    disabled: disabledProp,
    readOnly: readOnlyProp,
    required: requiredProp,
    hasError: hasErrorProp,
    errorText: errorTextProp,
    emptyValue = '',
    forwardError,
    ...other
  } = props as IWithAsProp<IFormFieldOwnProps<TFieldValues>>;

  const labeledContext = useContext(LabeledContext);
  const readOnly = readOnlyProp || labeledContext?.readOnly;
  const required = requiredProp || labeledContext?.required;
  const disabled = disabledProp || labeledContext?.disabled;

  const rules = {
    ...rulesProp,
    required: rulesProp?.required || required,
  };

  const { t } = useTranslation();
  const { field, fieldState } = useController({
    name,
    rules,
    shouldUnregister,
    defaultValue,
    control,
    disabled,
  });

  const { error: validationError } = fieldState;
  const validationErrorText = validationError
    ? getFormErrorText(validationError, rules, t)
    : undefined;

  const hasError =
    hasErrorProp || labeledContext?.hasError || !!validationError;
  const errorText =
    errorTextProp ?? labeledContext?.errorText ?? validationErrorText;
  const errorProps = forwardError ? { hasError, errorText } : undefined;

  const handleRef = useMergeRefs([field.ref, forwardedRef]);

  return (
    <Component
      {...other}
      {...field}
      {...errorProps}
      ref={handleRef}
      value={field.value ?? emptyValue}
      checked={!!field.value}
      disabled={disabled}
      readOnly={readOnly}
    />
  );
});
